if (!customElements.get("td-cart-progress")) {
  class TdCartProgress extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.THRESHOLD = this.calculateThresholdWithCurrencyConversion();
      this.TOTAL_THRESHOLD_ELM = this.querySelector("[data-total-threshold]");
      this.activeGifts = null;
      this.VARIANT_SELECTORS = this.querySelectorAll("[data-variant]");
      this.PROGRESS_BAR = this.querySelector("[data-progress-bar]");
      this.PROGRESS_DROPDOWN_WRAPPER = this.querySelector(
        "[data-progress-dropdown]"
      );
      this.PROGRESS_DROPDOWN_TRIGGER = this.querySelector(
        "[data-progress-dropdown-trigger]"
      );
      this.PROGRESS_DROPDOWN_CONTENT = this.querySelector(
        "[data-progress-dropdown-content]"
      );
      this.dropdownClosed = false;
      this.SELECTED_TEXT = this.querySelector("[data-selected]");
      this.loading = false;
      this.FREE_GIFTS_AMOUNT = parseInt(this.dataset.freeGiftsAmount, 10);
      this.VARIANT_REMOVE_BUTTONS = this.querySelectorAll(
        "[data-remove-variant]"
      );
      this.SWIPER_WRAPPER = this.querySelector("[data-swiper-wrapper]");
      if (
        !this.PROGRESS_BAR ||
        !this.PROGRESS_DROPDOWN_WRAPPER ||
        !this.PROGRESS_DROPDOWN_TRIGGER ||
        !this.PROGRESS_DROPDOWN_CONTENT ||
        !this.TOTAL_THRESHOLD_ELM ||
        !this.SWIPER_WRAPPER
      ) {
        return;
      }
      this.setThreshold();
      this.updateState();
      this.attachListeners();
    }

    maybeSetSwiperHeight() {
      // Callback needed to work around Swiper grid issue.
      // Reference: https://github.com/nolimits4web/swiper/discussions/7366
      const slideWIdth = this.SWIPER_WRAPPER.firstElementChild?.offsetWidth;
      if (slideWIdth > 0 && this.SWIPER_WRAPPER.dataset?.isGrid === "true") {
        this.SWIPER_WRAPPER.style.height = `${slideWIdth * 2 + 14}px`;
      }
    }

    calculateThresholdWithCurrencyConversion() {
      let threshold = parseInt(this.dataset?.threshold, 10);
      const currencyRate = parseFloat(window.Shopify.currency.rate);
      if (currencyRate !== 1) {
        // Calculate the threshold with currency rate based on the formula and fees referenced here:
        // https://help.shopify.com/en/manual/international/pricing/exchange-rates#auto-convert
        // https://help.shopify.com/en/manual/international/pricing/fees#conversion-rate-and-fee
        threshold = threshold * currencyRate * (1 + 0.02);
      }
      return threshold;
    }

    setThreshold() {
      this.TOTAL_THRESHOLD_ELM.innerHTML = Shopify.formatMoney(
        this.THRESHOLD,
        "${{amount_no_decimals}}"
      );
    }

    attachListeners() {
      this.VARIANT_SELECTORS.forEach((selector) => {
        selector.addEventListener("click", () => {
          if (
            this.loading ||
            this.activeGifts.length >= this.FREE_GIFTS_AMOUNT
          ) {
            return;
          }
          this.loading = true;
          this.updateFreeItemInCart(selector.dataset.variant, selector);
        });
      });
      document.addEventListener("updated-cart", () => {
        this.updateState();
      });
      this.PROGRESS_DROPDOWN_TRIGGER.addEventListener("click", () => {
        this.toggleDropdown();
      });
      this.VARIANT_REMOVE_BUTTONS.forEach((button) => {
        button.addEventListener("click", async () => {
          if (this.loading) {
            return;
          }
          const element = button.closest("[data-variant]");
          this.addLoadingState(element);
          this.loading = true;
          const variantId = button.dataset.removeVariant;
          const item = this.currentCart.items.find(
            (item) => item.variant_id == variantId
          );
          await this.removeItemFromCart(item).then(() => {
            this.loading = false;
            document.dispatchEvent(new CustomEvent("td:progress-updated"));
          });
        });
      });
      window.addEventListener("resize", () => {
        this.maybeSetSwiperHeight();
      });
      document.addEventListener("swiper:initialized", () => {
        this.maybeSetSwiperHeight();
      });
    }

    async updateState() {
      this.currentCart = await this.fetchCart();
      this.updateProgressBarState();
      this.updateVariantSelectorState();
      this.updateDropdownState();
      this.updateMessagingState();
      this.loading = false;
    }

    updateProgressBarState() {
      const cartSubtotal = this.currentCart.total_price;
      let progressPercentage = (cartSubtotal / this.THRESHOLD) * 100;
      if (progressPercentage > 100) {
        progressPercentage = 100;
      }

      this.PROGRESS_BAR.style.width = `${progressPercentage}%`;
    }

    updateVariantSelectorState() {
      if (this.currentCart.total_price >= this.THRESHOLD) {
        this.PROGRESS_DROPDOWN_WRAPPER.classList.add("td-active");
        this.activeGifts = this.currentCart.items.filter(
          (item) => item?.properties?._free_gift
        );
        if (this.activeGifts.length) {
          this.VARIANT_SELECTORS.forEach((selector) => {
            this.removeLoadingState(selector);
            if (selector.dataset.variant) {
              let variant = JSON.parse(selector.dataset.variant);
              let isActive = this.activeGifts.some(
                (gift) => gift.variant_id == variant.id
              );
              if (isActive) {
                selector.classList.add("td-active");
              } else {
                selector.classList.remove("td-active");
                this.maybeDisableVariantSelectors(selector);
              }
            } else {
              selector.classList.remove("td-active");
              this.maybeDisableVariantSelectors(selector);
            }
          });
        } else {
          this.VARIANT_SELECTORS.forEach((selector) => {
            selector.classList.remove("td-active");
            this.removeLoadingState(selector);
            this.maybeDisableVariantSelectors(selector);
          });
        }
        this.updateSelectedText();
      } else {
        this.PROGRESS_DROPDOWN_WRAPPER.classList.remove("td-active");
      }
    }

    maybeDisableVariantSelectors(selector) {
      //Update selectors when free gift amount is met
      if (this.activeGifts.length >= this.FREE_GIFTS_AMOUNT) {
        selector.classList.add("td-disabled");
      } else {
        selector.classList.remove("td-disabled");
      }
    }

    updateSelectedText() {
      if (this.activeGifts.length) {
        if (this.activeGifts.length > 1) {
          this.SELECTED_TEXT.innerHTML = this.activeGifts
            .map((gift) => gift.variant_title)
            .join("<br>");
        } else {
          this.SELECTED_TEXT.textContent = this.activeGifts[0].variant_title;
        }
      } else {
        this.SELECTED_TEXT.textContent = "None";
      }
    }

    updateMessagingState() {
      const preMessage = this.querySelector("[data-message]");
      const postMessage = this.querySelector("[data-post-message]");
      const amountLeftElm = this.querySelector("[data-amount-left]");
      if (!preMessage || !postMessage || !amountLeftElm) {
        return;
      }
      const amountLeft = this.THRESHOLD - this.currentCart.total_price;
      if (this.currentCart.total_price >= this.THRESHOLD) {
        preMessage.classList.remove("td-active");
        postMessage.classList.add("td-active");
      } else {
        preMessage.classList.add("td-active");
        postMessage.classList.remove("td-active");
        amountLeftElm.textContent = `${Shopify.formatMoney(
          amountLeft,
          "${{amount_no_decimals}}"
        )}`;
      }
    }

    updateDropdownState() {
      if (
        this.currentCart.total_price >= this.THRESHOLD &&
        this.activeGifts.length < this.FREE_GIFTS_AMOUNT
      ) {
        this.dropdownClosed = false;
        this.toggleDropdown();
      } else {
        this.dropdownClosed = true;
        setTimeout(() => {
          this.toggleDropdown();
        }, 500);
      }
    }

    toggleDropdown() {
      if (this.dropdownClosed) {
        this.PROGRESS_DROPDOWN_WRAPPER.classList.remove("td-open");
        this.dropdownClosed = false;
      } else {
        this.PROGRESS_DROPDOWN_WRAPPER.classList.add("td-open");
        this.dropdownClosed = true;
      }
    }

    async updateFreeItemInCart(variant, element) {
      this.addLoadingState(element);

      this.currentCart = await this.fetchCart();
      if (variant) {
        this.variantJSON = JSON.parse(variant);
        this.maybeAddItemToCart(this.variantJSON).then((d) => {
          document.dispatchEvent(new CustomEvent("td:progress-updated"));
        });
      }
    }

    async fetchCart() {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      return cart;
    }

    async removeItemFromCart(item) {
      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          throw new Error(
            `Error removing item from cart: ${response.statusText}`
          );
        }

        return response.json();
      } catch (error) {
        console.error(error);
        alert(
          "There was an error removing the item from the cart. Please try again."
        );
      }
    }

    async maybeAddItemToCart(variantJSON) {
      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: variantJSON.id,
                quantity: 1,
                properties: variantJSON.properties,
              },
            ],
          }),
        });

        if (!response.ok) {
          throw new Error(`Error adding item to cart: ${response.statusText}`);
        }

        return response.json();
      } catch (error) {
        console.error(error);
        alert(
          "There was an error adding the item to the cart. Please try again."
        );
      }
    }

    removeLoadingState(selector) {
      if (selector.classList.contains("td-loading")) {
        selector.classList.remove("td-loading");
      }
    }

    addLoadingState(selector) {
      if (!selector.classList.contains("td-loading")) {
        selector.classList.add("td-loading");
      }
    }
  }
  customElements.define("td-cart-progress", TdCartProgress);
}
